<script setup lang="ts">
import {
  computed,
  nextTick,
  toRef,
} from 'vue';

import type { IFormSelectInputOption } from '@modules/forms/types';
import VDropdownMenu from '@components/dropdowns/VDropdownMenu/VDropdownMenu.vue';
import { SUBAFFILIATE_HISTORY_CHANGES_ITEM_QUERY } from '@modules/affiliates-approved/submodules/trackers-settings/submodules/subaffiliate-condition/submodules/subaffiliate-history-changes-edit/constants';
import { FIXED_TRACKER_PAYMENTS_HISTORY_CHANGES_ITEM_QUERY } from '@modules/affiliates-approved/submodules/trackers-settings/submodules/trackers-list/submodules/fixed-tracker-payments/submodules/fixed-payments-history-edit/constants';
import { TRACKER_HISTORY_CHANGES_ITEM_QUERY } from '@modules/affiliates-approved/submodules/trackers-settings/submodules/trackers-list/submodules/tracker-deal-changes/submodules/tracker-history-changes-edit/constants';
import { CONFIG_QUERY_KEY } from '@modules/config/constants';
import { SETTINGS_BOOKKEEPING_GET_ITEM_QUERY_KEY } from '@modules/general-settings/submodules/bookkeeping/constants';
import { I18N_GET_TRANSLATIONS_QUERY_KEY } from '@modules/i18n/constants';
import { MERCHANT_SETTINGS_LIST_QUERY_KEY } from '@modules/merchant-settings/constants';
import { GET_MERCHANT_SETTINGS_QUERY_KEY } from '@modules/merchant-settings/submodules/edit/constants';
import { useMerchantStore } from '@modules/merchant/store';
import { PERMISSIONS_QUERY_KEY } from '@modules/permissions/constants';
import {
  REPORT_GENERATE_QUERY,
  REPORT_INNER_TAB_QUERY,
  REPORT_OPTIONS_QUERY,
  REPORT_STRUCTURE_STATES_QUERY,
  REPORT_TABS_QUERY,
} from '@modules/reports/constants';
import { TOKENS_QUERY_KEY } from '@modules/tokens/constants';
import { useQueryClient } from '@tanstack/vue-query';
import {
  assert,
  isString,
} from '@utils/guards';

const merchantStore = useMerchantStore();

const merchantList = toRef(merchantStore, 'merchantList');
const merchant = toRef(merchantStore, 'merchant');

const merchantId = computed(() => merchant.value?.id);
const queryClient = useQueryClient();

const merchantOptions = computed<IFormSelectInputOption<number>[]>(() => (
  merchantList.value.map((item) => {
    if (merchant.value?.id === item.id) {
      return {
        label: merchant.value.merchant_name,
        value: merchant.value.id,
      };
    }
    return {
      label: item.merchant_name,
      value: item.id,
    };
  })
));

async function updateMerchant(id: number): Promise<void> {
  const merchantItem = merchantList.value.find((item) => item.id === id);

  if (merchantItem && merchantId.value !== id) {
    merchantStore.updateMerchant(merchantItem);

    await nextTick();

    void queryClient.invalidateQueries({
      predicate: (query) => {
        const queryKey = query.queryKey[0];
        assert(isString(queryKey));

        return ![
          TOKENS_QUERY_KEY,
          CONFIG_QUERY_KEY,
          I18N_GET_TRANSLATIONS_QUERY_KEY,
          PERMISSIONS_QUERY_KEY,
          SETTINGS_BOOKKEEPING_GET_ITEM_QUERY_KEY,
          TRACKER_HISTORY_CHANGES_ITEM_QUERY,
          FIXED_TRACKER_PAYMENTS_HISTORY_CHANGES_ITEM_QUERY,
          SUBAFFILIATE_HISTORY_CHANGES_ITEM_QUERY,
          MERCHANT_SETTINGS_LIST_QUERY_KEY,
          GET_MERCHANT_SETTINGS_QUERY_KEY,
          REPORT_GENERATE_QUERY,
          REPORT_OPTIONS_QUERY,
          REPORT_STRUCTURE_STATES_QUERY,
          REPORT_INNER_TAB_QUERY,
          REPORT_TABS_QUERY,
        ].includes(queryKey);
      },
    });
  }
}
</script>

<template>
  <VDropdownMenu v-data-qa-auto-id="'TheMerchantSelector'"
    name="merchant"
    :options="merchantOptions"
    :value="merchantId"
    style-type="light"
    class="min-w-0"
    zero-min-width
    @change="updateMerchant"
  />
</template>
